import { useSyncExternalStore } from "react";

/**
 * Only one track plays at a time.
 *
 * Bandcamp's embed is a cross-origin iframe with no playback API to call, so
 * the only way to stop the running track is to take its iframe out of the page
 * -- which also puts the card's button back, ready to replay. That means
 * "which card is playing" has to be one piece of state above every card, not a
 * flag inside each of them.
 *
 * The Speed test is our own <audio>, so it can simply be paused; it registers
 * a stopper here for the same reason -- starting a Bandcamp embed should stop
 * it, and vice versa.
 */
let playing: string | null = null;
let stopAudio: (() => void) | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function play(key: string) {
  stopAudio?.();
  playing = key;
  emit();
}

/**
 * Take the Bandcamp iframe out of the page, and leave our own <audio> alone.
 *
 * These two have to be separable. The Speed test silences the embed when it
 * starts playing -- and if that call also ran the audio stopper, pressing play
 * would pause the thing that had just started, which is exactly what it did:
 * onPlay -> stop() -> stopAudio() -> pause(), before stop() ever reached its
 * early return. The player could never play.
 */
export function stopEmbed() {
  if (playing === null) return;
  playing = null;
  emit();
}

/** Silence everything -- the embed and the Speed test both. */
export function stop() {
  stopAudio?.();
  stopEmbed();
}

/**
 * An <audio> of ours -- the Speed test, the track list, a card's shop player
 * -- hands over a way to silence it. Returns the way to take it back, which
 * only clears the slot if it is still this stopper: a modal closing must not
 * unregister the player that opened underneath it in the meantime.
 */
export function registerAudio(stopper: (() => void) | null): () => void {
  stopAudio = stopper;
  return () => { if (stopAudio === stopper) stopAudio = null; };
}

export function usePlaying(): string | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => playing,
    () => null,
  );
}

/**
 * A Bandcamp player pasted into a shop's description, read back to the id it
 * names -- so it can be drawn with embedURL, the same size and colours as a
 * matched card's, rather than at whatever size the shop chose.
 */
export function bandcampEmbedFrom(url: string): { id: number; itemType: string } | null {
  const m = /bandcamp\.com\/EmbeddedPlayer\/.*?\b(album|track)=(\d+)/.exec(url);
  if (!m) return null;
  return { id: Number(m[2]), itemType: m[1] === "track" ? "t" : "a" };
}

/** Bandcamp's compact player, rebuilt from the id rather than stored per card. */
export function embedURL(albumId: number, itemType: string): string {
  const kind = itemType === "t" ? "track" : "album";
  return (
    `https://bandcamp.com/EmbeddedPlayer/${kind}=${albumId}/size=large` +
    `/bgcol=1e2229/linkcol=63b2ff/artwork=small/tracklist=false/transparent=true/`
  );
}

/**
 * iOS lets a page play audio only from a tap, and only on an element that tap
 * touched: an <audio> made later in an effect, once the clips have been
 * fetched, is refused with NotAllowedError however recent the tap was. So a
 * click that will lead to playback primes an element first -- load() inside
 * the gesture is what unlocks it -- and the player that mounts takes that
 * element instead of making its own. Harmless everywhere else.
 */
let primed: HTMLAudioElement | null = null;

export function primeAudio(): void {
  const el = primed ?? new Audio();
  el.preload = "none";
  unlock(el);
  primed = el;
}

export function takeAudio(): HTMLAudioElement {
  const el = primed ?? new Audio();
  primed = null;
  el.preload = "none";
  return el;
}

/** Touch an element inside a user gesture, so a later play() is allowed. */
export function unlock(el: HTMLAudioElement): void {
  try { el.load(); } catch { /* nothing loaded yet; the unlock is the point */ }
}

/** What to tell the reader when play() refused. */
export function playError(e: unknown): string {
  const err = e as { name?: string; message?: string } | null;
  if (err?.name === "NotAllowedError") return "tap ▶ again to start playback";
  return err?.message || "could not play";
}

/**
 * A fader shifts pitch along with tempo, as a turntable's does. Browsers
 * time-stretch by default -- playbackRate alone keeps the pitch -- and Safari
 * spells the switch its own way, so both are set every time the rate is.
 */
export function pitchWithRate(el: HTMLAudioElement, rate: number): void {
  el.playbackRate = rate;
  el.preservesPitch = false;
  (el as HTMLAudioElement & { webkitPreservesPitch?: boolean }).webkitPreservesPitch = false;
}

/**
 * Copy, and say whether it worked. The clipboard API exists only on HTTPS
 * and localhost, and Safari refuses it outside a tap; a copy that silently
 * did nothing while the page said "copied" is worse than no button.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the old way */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
